/**
 * Renders a non-interactive status card with label/value pairs.
 * Supports optional color coding per field (success, warning, error, info).
 *
 * Uses Deckard's theme palette tokens for proper light/dark mode support.
 */
import { Typography } from '@components/typography';
import { Box, Chip, Stack, useTheme } from '@mui/material';
import type { RichBlockProps } from './richBlockTypes';

export const StatusCardBlock = ({ block }: RichBlockProps) => {
  const { palette } = useTheme();
  const { title, fields = [] } = block.payload as {
    title?: string;
    fields: Array<{ label: string; value: string; color?: string }>;
  };

  /** Map color names from the registry to MUI chip colors */
  const chipColor = (color?: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    if (color && ['success', 'warning', 'error', 'info'].includes(color)) {
      return color as 'success' | 'warning' | 'error' | 'info';
    }
    return 'default';
  };

  return (
    <Stack mt={1} mb={2} gap={1} data-testid="rich-block-status-card">
      {title && (
        <Typography variant="subtitle1" color="agentChat.message.agent.foreground.default">
          {title}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '8px 12px',
          pb: 2,
          fontSize: '0.8125rem',
          overflow: 'hidden',
          overflowX: 'auto',
          alignItems: 'end',
        }}
      >
        {fields.map((field) => (
          <Box key={field.label} sx={{ display: 'contents' }}>
            <Typography
              variant="caption2"
              component="p"
              color="agentChat.message.agent.foreground.light"
            >
              {field.label}:
            </Typography>
            {field.color && field.color !== 'default' ? (
              <Chip
                label={field.value}
                size="small"
                color={chipColor(field.color)}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            ) : (
              <Typography variant="caption2" sx={{ color: palette.text.primary }}>
                {field.value}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Stack>
  );
};
